import type { FirefliesTranscriptDetail } from "@/types/fireflies";

const MAX_SENTENCES = 400;

function formatTimestamp(ms: number | null | undefined): string {
  if (ms == null || Number.isNaN(ms)) return "";
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${sec.toString().padStart(2, "0")}`;
}

function formatMeetingDate(dateMs: number | null | undefined): string {
  if (!dateMs) return "Unknown date";
  const d = new Date(dateMs);
  if (Number.isNaN(d.getTime())) return "Unknown date";
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function buildTranscriptBodyForAnalysis(detail: FirefliesTranscriptDetail): string {
  const sentences = (detail.sentences ?? []).slice(0, MAX_SENTENCES);
  const transcriptLines = sentences.map((s) => {
    const speaker = s.speaker_name || "Unknown";
    const time = formatTimestamp(s.start_time);
    const text = s.text?.trim() || "";
    return time ? `[${time}] ${speaker}: ${text}` : `${speaker}: ${text}`;
  });

  const attendees = (detail.meeting_attendees ?? [])
    .map((a) => a.displayName || a.name || a.email)
    .filter(Boolean);

  const parts: string[] = [];

  if (detail.summary?.overview) {
    parts.push(`Overview:\n${detail.summary.overview}`);
  } else if (detail.summary?.short_summary || detail.summary?.gist) {
    parts.push(`Overview:\n${detail.summary.short_summary || detail.summary.gist}`);
  }

  if (attendees.length > 0) {
    parts.push(`Attendees: ${attendees.join(", ")}`);
  } else if (detail.participants?.length) {
    parts.push(`Participants: ${detail.participants.join(", ")}`);
  }

  if (detail.duration != null) {
    parts.push(`Duration: ${Math.round(detail.duration)} minutes`);
  }

  if (transcriptLines.length > 0) {
    parts.push(`Transcript:\n${transcriptLines.join("\n")}`);
  }

  const truncated = (detail.sentences?.length ?? 0) > MAX_SENTENCES;
  if (truncated) {
    parts.push(
      `(Transcript truncated to first ${MAX_SENTENCES} of ${detail.sentences?.length} segments.)`
    );
  }

  return parts.join("\n\n");
}

export function formatMeetingDateForAnalysis(detail: FirefliesTranscriptDetail): string {
  return formatMeetingDate(detail.date);
}
